/**
 * Session Types Store - Centralized Session Type Management
 *
 * Replaces all hardcoded SESSION_TYPE_CONFIG patterns throughout the app.
 * Loads from database and provides:
 * - Type metadata (name, code, color, capacity)
 * - Color utilities for styling
 * - Fallback for unknown types
 *
 * @module useSessionTypesStore
 */

import { useMemo } from "react";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import type { SessionType } from "~/services/sessions/sessionTypesService";

// ============================================================================
// CONSTANTS
// ============================================================================

/** Validation limits */
export const SESSION_TYPE_LIMITS = {
  /** Maximum number of session types allowed */
  MAX_COUNT: 5,
  /** Maximum characters for session type name */
  NAME_MAX_LENGTH: 10,
  /** Maximum characters for session type code */
  CODE_MAX_LENGTH: 6,
  /** Maximum characters for description */
  DESCRIPTION_MAX_LENGTH: 100,
} as const;

/** Default fallback for unknown session types */
const FALLBACK_TYPE: SessionTypeDisplay = {
  code: "UNKNOWN",
  name: "미지정",
  shortName: "미지정",
  color: "#6366F1", // 보라색 (accent color)
  capacityLimit: null,
  capacityPriority: 0,
};

// ============================================================================
// TYPES
// ============================================================================

/** Display-ready session type info */
export interface SessionTypeDisplay {
  code: string;
  name: string;
  /** Truncated name for badges (max 4 chars) */
  shortName: string;
  /** Hex color from DB */
  color: string;
  /** Capacity limit (null = unlimited) */
  capacityLimit: number | null;
  /** Priority for capacity calculation */
  capacityPriority: number;
}

/** Color utilities derived from hex color */
export interface SessionTypeColors {
  /** Original hex color */
  hex: string;
  /** CSS text color class */
  textClass: string;
  /** CSS background class */
  bgClass: string;
  /** Subtle background class */
  bgSubtleClass: string;
  /** For inline styles */
  rgbValues: string;
}

interface SessionTypesState {
  /** Loaded session types from DB */
  types: SessionType[];
  /** Loading state */
  isLoading: boolean;
  /** Error message */
  error: string | null;

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────

  /** Set types (called from loader/hook) */
  setTypes: (types: SessionType[], isLoading?: boolean) => void;
  /** Set loading state */
  setLoading: (loading: boolean) => void;
  /** Set error */
  setError: (error: string | null) => void;

  // ─────────────────────────────────────────────────────────────────────────
  // SELECTORS (computed helpers)
  // ─────────────────────────────────────────────────────────────────────────

  /** Get type display info by code */
  getTypeByCode: (code: string) => SessionTypeDisplay;
  /** Get color utilities for a type */
  getTypeColors: (code: string) => SessionTypeColors;
  /** Get all active types as display format */
  getDisplayTypes: () => SessionTypeDisplay[];
  /** Check if a type code exists */
  hasType: (code: string) => boolean;
  /** Get default type code (first active type, or empty string) */
  getDefaultCode: () => string;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convert hex color to Tailwind-compatible classes
 * Maps common colors to semantic classes, falls back to inline styles
 */
function hexToColorClasses(hex: string): Omit<SessionTypeColors, "hex"> {
  const upper = hex.toUpperCase();

  // Map to semantic status colors where possible
  const colorMap: Record<string, Omit<SessionTypeColors, "hex">> = {
    // Reds/Pinks
    "#EF4444": { textClass: "text-error-ink", bgClass: "bg-error-fill", bgSubtleClass: "bg-error-tint", rgbValues: "239,68,68" },
    "#EC4899": { textClass: "text-error-ink", bgClass: "bg-error-fill", bgSubtleClass: "bg-error-tint", rgbValues: "236,72,153" },
    // Oranges/Yellows
    "#F97316": { textClass: "text-warning-ink", bgClass: "bg-warning-fill", bgSubtleClass: "bg-warning-tint", rgbValues: "249,115,22" },
    "#F59E0B": { textClass: "text-warning-ink", bgClass: "bg-warning-fill", bgSubtleClass: "bg-warning-tint", rgbValues: "245,158,11" },
    // Greens
    "#84CC16": { textClass: "text-success-ink", bgClass: "bg-success-fill", bgSubtleClass: "bg-success-tint", rgbValues: "132,204,22" },
    "#22C55E": { textClass: "text-green-ink", bgClass: "bg-green-fill", bgSubtleClass: "bg-green-tint", rgbValues: "34,197,94" },
    "#14B8A6": { textClass: "text-success-ink", bgClass: "bg-success-fill", bgSubtleClass: "bg-success-tint", rgbValues: "20,184,166" },
    "#10B981": { textClass: "text-success-ink", bgClass: "bg-success-fill", bgSubtleClass: "bg-success-tint", rgbValues: "16,185,129" },
    // Blues/Cyans
    "#06B6D4": { textClass: "text-info-ink", bgClass: "bg-info-fill", bgSubtleClass: "bg-info-tint", rgbValues: "6,182,212" },
    "#0EA5E9": { textClass: "text-sky-ink", bgClass: "bg-sky-fill", bgSubtleClass: "bg-sky-tint", rgbValues: "14,165,233" },
    "#3B82F6": { textClass: "text-info-ink", bgClass: "bg-info-fill", bgSubtleClass: "bg-info-tint", rgbValues: "59,130,246" },
    // Purples
    "#6366F1": { textClass: "text-violet-ink", bgClass: "bg-violet-fill", bgSubtleClass: "bg-violet-tint", rgbValues: "99,102,241" },
    "#8B5CF6": { textClass: "text-highlight-ink", bgClass: "bg-highlight-fill", bgSubtleClass: "bg-highlight-tint", rgbValues: "139,92,246" },
    "#A855F7": { textClass: "text-highlight-ink", bgClass: "bg-highlight-fill", bgSubtleClass: "bg-highlight-tint", rgbValues: "168,85,247" },
    "#D946EF": { textClass: "text-highlight-ink", bgClass: "bg-highlight-fill", bgSubtleClass: "bg-highlight-tint", rgbValues: "217,70,239" },
    // Grays
    "#64748B": { textClass: "text-muted", bgClass: "bg-elevated", bgSubtleClass: "bg-elevated/30", rgbValues: "100,116,139" },
  };

  if (colorMap[upper]) {
    return colorMap[upper];
  }

  // Fallback: parse hex and use neutral classes with inline color
  const rgb = hexToRgb(hex);
  return {
    textClass: "text-main",
    bgClass: "bg-elevated",
    bgSubtleClass: "bg-elevated/30",
    rgbValues: rgb ? `${rgb.r},${rgb.g},${rgb.b}` : "100,116,139",
  };
}

/** Parse hex color to RGB values */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result || !result[1] || !result[2] || !result[3]) return null;
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/** Convert SessionType to SessionTypeDisplay */
function toDisplayType(type: SessionType): SessionTypeDisplay {
  return {
    code: type.code,
    name: type.name,
    // Use code for badge display (shorter, more consistent)
    shortName: type.code,
    color: type.color,
    capacityLimit: type.capacityLimit,
    capacityPriority: type.capacityPriority,
  };
}

// ============================================================================
// STORE
// ============================================================================

export const useSessionTypesStore = create<SessionTypesState>()(
  subscribeWithSelector((set, get) => ({
    types: [],
    isLoading: true,
    error: null,

    // ─────────────────────────────────────────────────────────────────────────
    // ACTIONS
    // ─────────────────────────────────────────────────────────────────────────

    setTypes: (types, isLoading = false) => {
      set({ types, isLoading, error: null });
    },
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error, isLoading: false }),

    // ─────────────────────────────────────────────────────────────────────────
    // SELECTORS
    // ─────────────────────────────────────────────────────────────────────────

    getTypeByCode: (code) => {
      const { types } = get();
      const found = types.find((t) => t.code === code);
      if (found) {
        return toDisplayType(found);
      }
      // Return fallback with the original code (no truncation)
      return { ...FALLBACK_TYPE, code, name: code, shortName: code };
    },

    getTypeColors: (code) => {
      const { types } = get();
      const found = types.find((t) => t.code === code);
      const hex = found?.color || FALLBACK_TYPE.color;
      return {
        hex,
        ...hexToColorClasses(hex),
      };
    },

    getDisplayTypes: () => {
      const { types } = get();
      return types.filter((t) => t.isActive).map(toDisplayType);
    },

    hasType: (code) => {
      const { types } = get();
      return types.some((t) => t.code === code);
    },

    getDefaultCode: () => {
      const { types } = get();
      return types[0]?.code ?? "";
    },
  }))
);

// ============================================================================
// CONVENIENCE HOOKS (for common use cases)
// ============================================================================

/** Get type display info by code */
export const useSessionTypeByCode = (code: string) =>
  useSessionTypesStore((state) => state.getTypeByCode(code));

/**
 * Get all display types.
 * getDisplayTypes()는 매 호출 새 배열을 만들므로 selector로 직접 쓰면
 * zustand v5(useSyncExternalStore)에서 무한 렌더가 발생한다.
 * 안정 상태(types)를 구독하고 useMemo로 변환 결과를 캐시한다.
 */
export const useDisplaySessionTypes = () => {
  const rawTypes = useSessionTypesStore((state) => state.types);
  return useMemo(
    () => useSessionTypesStore.getState().getDisplayTypes(),
    [rawTypes]
  );
};

/** Get type colors by code */
export const useSessionTypeColors = (code: string) =>
  useSessionTypesStore((state) => state.getTypeColors(code));

/** Get default session type code (first active type) */
export const useDefaultSessionTypeCode = () =>
  useSessionTypesStore((state) => state.types[0]?.code ?? "");

export default useSessionTypesStore;
